import { Document } from '@/Data/Document/DocumentDataClass'
import { blobToBinary } from '@/utils/blobToBinary'

export interface UploadDocumentsOptions {
  onProgress?: (completed: number, total: number, filename?: string) => void
  onError?: (error: unknown, filename?: string) => void
  afterCreate?: (documentId: string) => Promise<void>
}

export type DocumentsUploadOptions = UploadDocumentsOptions & {
  /**
   * Allow uploading documents even when there is no relation target.
   * Consumers that support relations can ignore this flag.
   */
  allowNoRelation?: boolean
}

/**
 * Upload files sequentially by creating Document items.
 * Consumers can optionally attach relations via `afterCreate`.
 */
export const uploadDocumentsSequential = async (
  files: File[],
  opts?: UploadDocumentsOptions,
) => {
  if (!files?.length) return

  const attachmentsBinary = await Promise.all(
    files.map((file) => blobToBinary(file)),
  )

  let completed = 0
  const total = attachmentsBinary.length

  for (const binary of attachmentsBinary) {
    try {
      const document = await Document.create({
        body: binary,
        title: binary.filename,
        folder: false,
      })
      const id = document?.id()
      if (id) {
        await opts?.afterCreate?.(id)
      }
    } catch (e) {
      opts?.onError?.(e, binary.filename)
    } finally {
      completed += 1
      opts?.onProgress?.(completed, total, binary.filename)
    }
  }
}

export default uploadDocumentsSequential
